import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { useSidebar } from "../contexts/SidebarContext";
import { navigationItems, navigationIcons } from "../config/navigation";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import {
  useConversationList,
  useRenameConversation,
  useDeleteConversation
} from "@/hooks/useReportConversations";
import {
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const {
  ChevronDownIcon,
  ChevronRightIcon,
  PanelLeftIcon,
  PanelRightIcon,
  LifeBuoyIcon,
  SendIcon,
} = navigationIcons;

const defaultExpandedItems = navigationItems
  .filter(item => item.hasSubmenu && item.isExpanded)
  .map(item => item.id);

const bottomItems = [
  {
    id: "support",
    label: "Support",
    icon: LifeBuoyIcon,
  },
  {
    id: "feedback",
    label: "Feedback",
    icon: SendIcon,
  },
];

export const AppSidebar = (): JSX.Element => {
  const [expandedItems, setExpandedItems] = useState<string[]>(defaultExpandedItems);
  const { isCollapsed, toggleSidebar, setIsCollapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isSuperAdmin } = useAuth();

  // Conversation management
  const { data: conversations } = useConversationList();
  const renameMutation = useRenameConversation();
  const deleteMutation = useDeleteConversation();

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Dialog handlers
  const handleRenameClick = (id: string, currentTitle: string) => {
    setRenamingId(id);
    setNewName(currentTitle);
  };

  const confirmRename = async () => {
    if (renamingId && newName.trim()) {
      await renameMutation.mutateAsync({ id: renamingId, title: newName.trim() });
      setRenamingId(null);
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeletingId(id);
  };

  const confirmDelete = async () => {
    if (deletingId) {
      await deleteMutation.mutateAsync(deletingId);
      // If we are currently on this report, navigate away
      if (location.pathname === `/reports/${deletingId}`) {
        navigate('/reports');
      }
      setDeletingId(null);
    }
  };

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };


  const handleNavigation = (path?: string) => {
    if (path) {
      // Special handling for review link
      if (path === "/invoices/processing?review=all") {
        navigate("/invoices/processing", { state: { triggerReviewAll: true } });
      } else {
        navigate(path);
      }
    }
  };

  const isActiveRoute = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const isSubmenuActive = (submenuItems?: Array<{ path: string }>, itemId?: string) => {
    if (!submenuItems) return false;
    // For invoices, also consider the dashboard route as active
    if (itemId === 'invoices' && location.pathname === '/invoices/dashboard') {
      return true;
    }
    return submenuItems.some(item =>
      location.pathname === item.path ||
      location.pathname.startsWith(`${item.path}/`)
    );
  };

  // Compute navigation items with dynamic conversations
  const navItems = navigationItems.map(item => {
    if (item.id === 'reports' && conversations) {
      // Create dynamic submenu items from conversations
      const conversationItems = conversations.map(c => ({
        id: c.id,
        label: c.title,
        path: `/reports/${c.id}`,
        isDynamic: true, // Marker for custom rendering
        title: c.title,
      }));

      return {
        ...item,
        submenuItems: [
          ...(item.submenuItems || []),
          ...conversationItems
        ]
      };
    }
    return item;
  });

  return (
    <nav className={`flex flex-col min-h-screen bg-neutral-50 border-r border-neutral-200 transition-all duration-300 ${isCollapsed ? 'w-[60px]' : 'w-[255px]'
      }`}>
      {/* Header Section */}
      <header className={`flex flex-col items-start gap-2 py-2 w-full transition-all duration-300 ${isCollapsed ? 'px-2' : 'px-4'
        }`}>
        <div className={`flex items-center gap-2 p-2 w-full ${isCollapsed ? 'justify-center' : ''
          }`}>
          {!isCollapsed && (
            <div className="w-8 h-8 bg-[#ffaa00] rounded-lg border border-solid border-[#e0e0e0]" />
          )}
          {!isCollapsed && (
            <div className="flex flex-col items-start flex-1">
              <h1 className="font-semibold text-neutral-950 text-sm">
                Demo
              </h1>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="w-4 h-4 p-0 hover:bg-neutral-100"
            onClick={toggleSidebar}
          >
            {isCollapsed ? (
              <PanelRightIcon className="w-4 h-4" />
            ) : (
              <PanelLeftIcon className="w-4 h-4" />
            )}
          </Button>
        </div>
      </header>

      {/* Main Navigation */}
      <main className="flex flex-col items-start flex-1 w-full">
        <section className={`flex flex-col items-start py-2 w-full transition-all duration-300 ${isCollapsed ? 'px-2' : 'px-4'
          }`}>
          {/* Platform Label */}
          {!isCollapsed && (
            <div className="flex h-8 items-center gap-2 px-2 py-0 w-full opacity-70">
              <span className="font-medium text-neutral-950 text-xs">
                Platform
              </span>
            </div>
          )}

          {/* Navigation Items */}
          <div className="flex flex-col items-start gap-1 w-full">
            {navItems.filter(item => !item.adminOnly || isAdmin()).map((item) => {
              const visibleSubmenuItems = item.submenuItems?.filter(subItem => {
                const sub = subItem as { adminOnly?: boolean; superAdminOnly?: boolean };
                if (sub.superAdminOnly) return isSuperAdmin();
                if (sub.adminOnly) return isAdmin();
                return true;
              });

              return (
                <div key={item.id} className="flex flex-col items-start w-full">
                  {item.hasSubmenu ? (
                    <Collapsible
                      open={expandedItems.includes(item.id)}
                      onOpenChange={() => toggleExpanded(item.id)}
                      className="w-full"
                    >
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            if (isCollapsed) {
                              // When collapsed, expand the sidebar and the item
                              e.preventDefault();
                              setIsCollapsed(false);
                              setExpandedItems(prev => [...prev.filter(id => id !== item.id), item.id]);
                            } else {
                              // When expanded, navigate if there's a path
                              if (item.path) handleNavigation(item.path);
                            }
                          }}
                          className={`flex h-8 items-center gap-2 p-2 w-full rounded-md justify-center hover:bg-neutral-100 transition-colors ${isSubmenuActive(visibleSubmenuItems, item.id) || (item.id === 'invoices' && location.pathname === '/invoices/dashboard') ? "bg-neutral-100 text-[#0000ff]" : ""
                            } ${isCollapsed ? 'justify-center' : 'justify-start'}`}
                        >
                          <item.icon className={`w-4 h-4 ${isSubmenuActive(visibleSubmenuItems, item.id) || (item.id === 'invoices' && location.pathname === '/invoices/dashboard') ? "text-[#0000ff]" : ""
                            }`} />
                          {!isCollapsed && (
                            <span className={`font-regular text-sm ${isSubmenuActive(visibleSubmenuItems, item.id) || (item.id === 'invoices' && location.pathname === '/invoices/dashboard') ? "text-[#0000ff]" : "text-neutral-950"
                              }`}>
                              {item.label}
                            </span>
                          )}
                          {!isCollapsed && (
                            <div className="flex items-center justify-end flex-1">
                              {expandedItems.includes(item.id) ? (
                                <ChevronDownIcon className="w-4 h-4" />
                              ) : (
                                <ChevronRightIcon className="w-4 h-4" />
                              )}
                            </div>
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      {!isCollapsed && visibleSubmenuItems && visibleSubmenuItems.length > 0 && (
                        <CollapsibleContent className="w-full">
                          <div className="flex items-start px-3.5 py-0 w-full">
                            <div className="flex flex-col items-start gap-1 py-0.5 flex-1 min-w-0 border-l border-solid border-neutral-300">
                              {visibleSubmenuItems.map((subItem) => (
                                <div key={subItem.id} className="relative group w-full">
                                  <Button
                                    variant="ghost"
                                    onClick={() => handleNavigation(subItem.path)}
                                    className={`flex h-7 items-center gap-2 px-2 py-0 w-full rounded-md justify-start hover:bg-neutral-100 transition-colors ${isActiveRoute(subItem.path) ? "text-[#0000ff] bg-neutral-100" : ""
                                      } ${(subItem as any).isDynamic ? "pr-6" : ""}`}
                                  >

                                    <span className={`font-regular text-sm truncate flex-1 min-w-0 text-left ${isActiveRoute(subItem.path) ? "text-[#0000ff]" : "text-neutral-950"
                                      }`}>
                                      {subItem.label}
                                    </span>
                                  </Button>
                                  {(subItem as any).isDynamic && (
                                    <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-neutral-200">
                                            <MoreHorizontal className="h-3 w-3 text-neutral-500" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-40 z-50">
                                          <DropdownMenuItem onClick={(e) => {
                                            e.stopPropagation();
                                            handleRenameClick(subItem.id, subItem.label);
                                          }}>
                                            <Pencil className="mr-2 h-3.5 w-3.5" />
                                            <span>Rename</span>
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            className="text-red-600 focus:text-red-600"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteClick(subItem.id);
                                            }}
                                          >
                                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                                            <span>Delete</span>
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </CollapsibleContent>
                      )}
                    </Collapsible>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => handleNavigation(item.path)}
                      className={`flex h-8 items-center gap-2 p-2 w-full rounded-md justify-center hover:bg-neutral-100 transition-colors ${isActiveRoute(item.path) ? "bg-neutral-100 text-[#0000ff]" : ""
                        } ${isCollapsed ? 'justify-center' : 'justify-start'}`}
                    >
                      <item.icon className={`w-4 h-4 ${isActiveRoute(item.path) ? "text-[#0000ff]" : ""
                        }`} />
                      {!isCollapsed && (
                        <span className={`font-regular text-sm ${isActiveRoute(item.path) ? "text-[#0000ff]" : "text-neutral-950"
                          }`}>
                          {item.label}
                        </span>
                      )}
                      {!isCollapsed && (
                        <div className="flex items-center justify-end flex-1">
                          <ChevronRightIcon className="w-4 h-4" />
                        </div>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Bottom Section */}
        <section className={`flex flex-col items-start justify-end gap-1 py-2 flex-1 w-full transition-all duration-300 ${isCollapsed ? 'px-2' : 'px-4'
          }`}>
          {bottomItems.map((item) => (
            <Button
              key={item.id}
              variant="ghost"
              className={`flex h-8 items-center gap-2 p-2 w-full rounded-md justify-center hover:bg-neutral-100 transition-colors ${isCollapsed ? 'justify-center' : 'justify-start'
                }`}
            >
              <item.icon className="w-4 h-4" />
              {!isCollapsed && (
                <span className="font-regular text-neutral-950 text-sm">
                  {item.label}
                </span>
              )}
            </Button>
          ))}
        </section>
      </main>

      {/* Rename Dialog */}
      <Dialog open={!!renamingId} onOpenChange={(open) => !open && setRenamingId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Report</DialogTitle>
            <DialogDescription>
              Enter a new name for your report.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2 py-4">
            <div className="grid flex-1 gap-2">
              <Label htmlFor="name" className="sr-only">
                Name
              </Label>
              <Input
                id="name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmRename();
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="justify-start sm:justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setRenamingId(null)}>
              Cancel
            </Button>
            <Button type="button" onClick={confirmRename}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deletingId} onOpenChange={(open) => !open && setDeletingId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Report</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this report? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-start sm:justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setDeletingId(null)}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </nav>
  );
};
